import { useLocale } from '../../application/i18n/LocaleProvider';
import { ChevronDownIcon } from './BrandIcons';
import { InfoIcon } from '../icons/ControlIcons';

/**
 * The status list at the foot of the drawer (SPEC-018).
 *
 * Split from `AppDrawer` because it is the half that only READS. The drawer
 * above it owns two controls and the state behind them; this owns a table and
 * has no state at all, which is also why it is the piece that can be rendered
 * from a plain array in a test.
 *
 * Every row is something a visitor could reasonably act on or reasonably worry
 * about. `buildStatusRows` decides which rows exist and what they mean — the
 * choice not to ship habb.me's fuller diagnostics list is argued there.
 */

/** `—`, because a row whose value is unknown must still be a row. */
const UNKNOWN = '—';

/**
 * "Just now", or a real date once it is old enough to be worth one.
 *
 * `Intl` is given the app's locale rather than the browser's: the drawer is
 * chrome, and chrome follows the URL like everything else here (CON-002).
 */
function formatCheckedAt(timestamp, localeCode, t) {
    if (!timestamp) {
        return t('settings.value.never');
    }

    const minutesAgo = Math.round((Date.now() - timestamp) / 60000);

    if (minutesAgo < 1) {
        return t('settings.value.justNow');
    }

    if (minutesAgo < 60) {
        return t('settings.value.minutesAgo', { count: minutesAgo });
    }

    try {
        return new Intl.DateTimeFormat(localeCode.replace('-', '-'), {
            dateStyle: 'medium',
            timeStyle: 'short',
        }).format(new Date(timestamp));
    } catch (error) {
        // An engine that cannot format it still has something true to say.
        return new Date(timestamp).toISOString().slice(0, 16).replace('T', ' ');
    }
}

function rowValue(row, localeCode, t) {
    if (typeof row.booleanValue === 'boolean') {
        return t(row.booleanValue ? 'settings.value.yes' : 'settings.value.no');
    }

    if (row.valueKey) {
        return t(row.valueKey);
    }

    if (row.timestamp !== undefined) {
        return formatCheckedAt(row.timestamp, localeCode, t);
    }

    return row.value || UNKNOWN;
}

/**
 * Closed by default, and that is the point (SPEC-018 D-9).
 *
 * Seven rows of diagnostics cost 353px of a 457px window — a third of the whole
 * panel, spent on the one thing here nobody opens the drawer to read. Collapsed
 * it costs a line, and the answers are one tap away for the visit where they
 * matter.
 *
 * A real `<details>`, so the open state, the keyboard, the screen reader and
 * find-in-page are the platform's job rather than a `useState` and an
 * `aria-expanded` that has to be remembered.
 */
export default function AppDrawerStatus({ rows }) {
    const { t, localeCode } = useLocale();

    return (
        <details className="app-setting" data-setting="status">
            {/* The same summary the three settings above it wear (D-10), so the
                drawer reads as four rows of one kind rather than three of one
                and something else. It carries no state word: this section is
                what the app can TELL you, not something you can change. */}
            <summary className="app-setting__summary app-setting__summary--stateless">
                <span className="app-setting__glyph" aria-hidden="true"><InfoIcon size={22} /></span>
                <h3 className="app-setting__label">{t('settings.status.title')}</h3>
                <ChevronDownIcon className="app-setting__caret" size={16} />
            </summary>

            <dl className="app-status__list app-setting__detail">
                {rows.map((row) => (
                    <div className="app-status__row" key={row.key}>
                        <dt className="app-status__label">{t(row.labelKey)}</dt>
                        <dd className="app-status__value">
                            {rowValue(row, localeCode, t)}
                            {row.hintKey ? <span className="app-status__hint">{t(row.hintKey)}</span> : null}
                        </dd>
                    </div>
                ))}
            </dl>
        </details>
    );
}
